// EAN-13 helpers for store-generated product / variant barcodes.
//
// A shop that prints its own labels has no GS1 company prefix, so the codes
// here start from an "in-store" prefix (GS1 reserves 20–29 for internal use —
// the same range the weighable "2…" barcodes already use, so 21 is the default
// for product labels). The last digit is the standard mod-10 check digit, which
// is what makes an ordinary scanner accept the label.

export const DEFAULT_EAN_PREFIX = '21';

// Mod-10 check digit: weight the first 12 digits 1,3,1,3… and round the sum up
// to the next multiple of ten.
export function ean13CheckDigit(first12: string): number {
  const sum = first12
    .slice(0, 12)
    .split('')
    .reduce((acc, ch, i) => acc + Number(ch) * (i % 2 === 0 ? 1 : 3), 0);
  return (10 - (sum % 10)) % 10;
}

// Build a full 13-digit code from a prefix and a running serial. The serial is
// zero-padded on the LEFT so every serial gets its own code: right-padding it
// would collapse 3, 30 and 300 onto the same body ("213000000000") and hand two
// different products the same barcode.
export function makeEan13(serial: number, prefix: string = DEFAULT_EAN_PREFIX): string {
  const width = 12 - prefix.length;
  const n = Math.max(0, Math.trunc(serial));
  const body = `${prefix}${String(n).padStart(width, '0').slice(-width)}`;
  return `${body}${ean13CheckDigit(body)}`;
}

export function isValidEan13(code: string): boolean {
  if (!/^\d{13}$/.test(code)) return false;
  return Number(code[12]) === ean13CheckDigit(code.slice(0, 12));
}

// Allocate `count` codes that collide with nothing in `taken`. Serials continue
// from the highest one already issued under this prefix, so re-generating a
// product's variants never reuses a code that is already on a printed label.
export function nextEan13Batch(
  taken: string[],
  count: number,
  prefix: string = DEFAULT_EAN_PREFIX
): string[] {
  const used = new Set<string>();
  let highest = 0;
  taken.forEach((code) => {
    if (!code) return;
    used.add(code);
    if (code.startsWith(prefix) && code.length === 13) {
      const serial = parseInt(code.slice(prefix.length, 12), 10);
      if (!Number.isNaN(serial) && serial > highest) highest = serial;
    }
  });
  const out: string[] = [];
  let serial = highest;
  while (out.length < count) {
    serial += 1;
    const code = makeEan13(serial, prefix);
    if (used.has(code)) continue;
    used.add(code);
    out.push(code);
  }
  return out;
}

// Every barcode currently in use, so a new batch can avoid all of them.
export function collectBarcodes(
  products: { barcode?: string; variants?: { barcode?: string }[] }[]
): string[] {
  const out: string[] = [];
  products.forEach((p) => {
    if (p.barcode) out.push(p.barcode);
    p.variants?.forEach((v) => {
      if (v.barcode) out.push(v.barcode);
    });
  });
  return out;
}
